import { useMemo } from 'react';

import { useDataProvider } from '@work4all/data';

import { Customer } from '@work4all/models/lib/Classes/Customer.entity';
import { Project } from '@work4all/models/lib/Classes/Project.entity';
import { Supplier } from '@work4all/models/lib/Classes/Supplier.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { LookupType } from '@work4all/models/lib/Enums/LookupType.enum';

import { useOverlayTemplate } from '../../../hooks/use-overlay-template';
import { MaskExtendedConfig } from '../../../types';

// Default project status id for new projects: '01 Ungeplant'
const DEFAULT_PROJECT_STATUS_ID = 20214219;

export const useProjectDefaultData = (mask: MaskExtendedConfig): Project => {
  const requestData = useMemo<DataRequest>(() => {
    return {
      entity: Entities.lookUp,
      data: { id: null, name: null },
      completeDataResponse: true,
      vars: {
        type: LookupType.PROJEKT_STATI,
      },
    };
  }, []);

  const template = useOverlayTemplate<Customer | Supplier>({ mask });
  const response = useDataProvider(requestData);

  const defaultProjectStatus = useMemo(() => {
    return response?.data?.find(
      (status) => status.id === DEFAULT_PROJECT_STATUS_ID
    );
  }, [response?.data]);

  const { presetFields = '{}' } = mask.params;

  return useMemo<Project>(() => {
    const parsedPresetFields = JSON.parse(presetFields);
    const project: Project = { ...parsedPresetFields };

    project.startDateOuter = new Date().toISOString();
    project.projectStatus = defaultProjectStatus ?? null;
    project.projectStatusId = defaultProjectStatus?.id ?? 0;

    const contact = template?.data?.mainContact;
    if (template.sourceEntity === Entities.customer) {
      project.customer = template?.data;
      project.customerId = template?.data?.id;
      project.customerContact = contact;
      project.customerContactId = contact?.id;
    } else if (template.sourceEntity === Entities.supplier) {
      project.supplier = template?.data;
      project.supplierId = template?.data?.id;
      project.supplierContact = contact;
      project.supplierContactId = contact?.id;
    }

    return project;
  }, [defaultProjectStatus, presetFields, template]);
};
